import { useState, useEffect, useCallback, useRef } from 'react';
import { Calculator, Plus, Trash2, ChevronDown, User, Ruler, Layers, FileText, Package, Settings } from 'lucide-react';
import { getAvailableSizes, performMultiSizeCalculation, getConfiguredProducts } from '../../api/calculator';
import { getCustomers } from '../../api/customers';

const UNITS = [
  { value: 'meters', label: 'Meters (m)', factor: 1 },
  { value: 'centimeters', label: 'Centimeters (cm)', factor: 0.01 },
  { value: 'feet', label: 'Feet (ft)', factor: 0.3048 },
  { value: 'inches', label: 'Inches (in)', factor: 0.0254 }
];

const ALLOCATION_TYPES = [
  { value: 'area', label: '% of Area' },
  { value: 'cartons', label: 'Direct Cartons' }
];

// Searchable Customer Input Component
const CustomerSearchInput = ({ customers, selectedCustomerId, onSelect }) => {
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const dropdownRef = useRef(null);

  // Set initial value if customer is selected
  useEffect(() => {
    if (selectedCustomerId && customers.length > 0) {
      const customer = customers.find(c => c.id === selectedCustomerId);
      if (customer) {
        setSelectedCustomer(customer);
        setSearchText(customer.full_name);
      }
    }
  }, [selectedCustomerId, customers]);

  // Filter customers based on search
  const filteredCustomers = searchText.trim()
    ? customers.filter(c => 
        c.full_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        c.phone?.includes(searchText)
      )
    : customers;

  const handleInputChange = (e) => {
    setSearchText(e.target.value);
    setShowDropdown(true);
    // Clear selection if typing after selection
    if (selectedCustomer && e.target.value !== selectedCustomer.full_name) {
      setSelectedCustomer(null);
      onSelect(null, e.target.value);
    }
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setSearchText(customer.full_name);
    setShowDropdown(false);
    onSelect(customer.id, customer.full_name);
  };

  const handleClear = () => {
    setSearchText('');
    setSelectedCustomer(null);
    setShowDropdown(false);
    onSelect(null, '');
  };

  // Handle blur with delay to allow click on dropdown items
  const handleBlur = (e) => {
    // Check if the related target is within the dropdown
    if (dropdownRef.current && dropdownRef.current.contains(e.relatedTarget)) {
      return; // Don't close if clicking within dropdown
    }
    setTimeout(() => setShowDropdown(false), 300);
  };

  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-medium text-primary-slate mb-2">
        <User className="w-4 h-4" />
        Customer (Optional)
        <span className="text-xs font-normal">({customers.length} available)</span>
      </label>
      <div className="relative">
        <input
          type="text"
          value={searchText}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          onBlur={handleBlur}
          placeholder="Type customer name or select from list..."
          className="w-full px-4 py-3 pr-10 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold bg-white"
        />
        {searchText && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xl"
          >
            ×
          </button>
        )}
        
        {/* Dropdown */}
        {showDropdown && (
          <div 
            ref={dropdownRef}
            className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-secondary-lightGray rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            {customers.length === 0 ? (
              <div className="px-4 py-3 text-sm text-primary-slate">
                Loading customers...
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="px-4 py-3 text-sm text-primary-slate">
                No matching customers for "{searchText}"
              </div>
            ) : (
              filteredCustomers.slice(0, 20).map(customer => (
                <button
                  key={customer.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()} // Prevent blur
                  onClick={() => handleSelectCustomer(customer)}
                  className="w-full px-4 py-3 text-left hover:bg-accent-gold/10 flex items-center justify-between border-b border-secondary-lightGray last:border-0 cursor-pointer"
                >
                  <span className="font-medium text-primary-black">{customer.full_name}</span>
                  {customer.phone && (
                    <span className="text-sm text-primary-slate">{customer.phone}</span>
                  )}
                </button>
              ))
            )}
            {filteredCustomers.length > 20 && (
              <div className="px-4 py-2 text-xs text-primary-slate bg-secondary-offWhite">
                Showing 20 of {filteredCustomers.length} matches
              </div>
            )}
          </div>
        )}
      </div>
      {selectedCustomer && (
        <p className="text-xs text-green-600 mt-1">✓ Customer: {selectedCustomer.full_name}</p>
      )}
      {!selectedCustomer && searchText && (
        <p className="text-xs text-primary-slate mt-1">Customer name: {searchText} (not linked to account)</p>
      )}
    </div>
  );
};

const MultiSizeTileCalculatorForm = ({ 
  onCalculationComplete, 
  onConfigure,
  calculatorMode = 'multi' // 'multi' or 'legacy'
}) => {
  // Available data
  const [sizes, setSizes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [configuredProducts, setConfiguredProducts] = useState([]);
  const [loadingSizes, setLoadingSizes] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    measurements: [
      {
        id: Date.now(),
        size_id: null,
        size_name: '',
        pieces_per_carton: 4,
        tile_length_mm: 400,
        tile_width_mm: 400,
        allocation_type: 'area',
        allocation_value: 100,
        isMainFloor: true,
        useProduct: false,
        product_id: null,
        product_code: ''
      }
    ],
    area_length: '',
    area_width: '',
    unit: 'meters',
    wastage_percent: 10,
    customer_id: null
  });
  
  const [errors, setErrors] = useState({});
  const [calculating, setCalculating] = useState(false);

  // Load sizes
  useEffect(() => {
    const loadSizes = async () => {
      try {
        setLoadingSizes(true);
        const response = await getAvailableSizes();
        if (response.success) {
          setSizes(response.data);
        }
      } catch (error) {
        console.error('Failed to load sizes:', error);
      } finally {
        setLoadingSizes(false);
      }
    };
    loadSizes();
  }, []);

  // Load customers
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const response = await getCustomers({ limit: 200 });
        console.log('Customers API response:', response);
        
        // Handle various response formats
        let customerData = [];
        
        // Check direct response properties first (response.customers)
        if (response.customers && Array.isArray(response.customers)) {
          customerData = response.customers;
        } 
        // Then check nested in data (response.data.customers)
        else if (response.success && response.data) {
          if (Array.isArray(response.data)) {
            customerData = response.data;
          } else if (response.data.customers) {
            customerData = response.data.customers;
          }
        }
        // Direct array at root
        else if (Array.isArray(response)) {
          customerData = response;
        }
        
        console.log('Loaded customers:', customerData.length);
        setCustomers(customerData);
      } catch (error) {
        console.error('Failed to load customers:', error);
      }
    };
    loadCustomers();
  }, []);

  // Load configured products
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoadingProducts(true);
        const response = await getConfiguredProducts({ limit: 100 });
        if (response.success) {
          setConfiguredProducts(response.data || response.products || []);
        }
      } catch (error) {
        console.error('Failed to load configured products:', error);
      } finally {
        setLoadingProducts(false);
      }
    };
    loadProducts();
  }, []);

  // Handle size selection
  const handleSizeChange = useCallback((measurementId, sizeId) => {
    const selectedSize = sizes.find(s => s.id === parseInt(sizeId));
    
    setFormData(prev => ({
      ...prev,
      measurements: prev.measurements.map(m => 
        m.id === measurementId
          ? {
              ...m,
              size_id: selectedSize?.id || null,
              size_name: selectedSize?.dimension || '',
              pieces_per_carton: selectedSize?.default_pieces_per_carton || 4,
              tile_length_mm: selectedSize?.tile_length_mm || 400,
              tile_width_mm: selectedSize?.tile_width_mm || 400
            }
          : m
      )
    }));
  }, [sizes]);

  // Handle measurement field change
  const handleMeasurementChange = useCallback((measurementId, field, value) => {
    setFormData(prev => ({
      ...prev,
      measurements: prev.measurements.map(m => 
        m.id === measurementId
          ? { ...m, [field]: value }
          : m
      )
    }));
  }, []);

  // Add design tile
  const handleAddDesignTile = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      measurements: [
        ...prev.measurements,
        {
          id: Date.now(),
          size_id: null,
          size_name: '',
          pieces_per_carton: 4,
          tile_length_mm: 400,
          tile_width_mm: 400,
          allocation_type: 'area',
          allocation_value: 10, // Default to 10% for design tiles
          isMainFloor: false,
          useProduct: false,
          product_id: null,
          product_code: ''
        }
      ]
    }));
  }, []);

  // Handle product selection for a measurement
  const handleProductChange = useCallback((measurementId, productId) => {
    const selectedProduct = configuredProducts.find(p => p.id === parseInt(productId));
    
    setFormData(prev => ({
      ...prev,
      measurements: prev.measurements.map(m => 
        m.id === measurementId
          ? {
              ...m,
              product_id: selectedProduct?.id || null,
              product_code: selectedProduct?.product_code || '',
              // Auto-fill from product config
              size_id: null,
              size_name: selectedProduct?.size_name || '',
              pieces_per_carton: selectedProduct?.pieces_per_carton || 4,
              tile_length_mm: selectedProduct?.tile_length_mm || 400,
              tile_width_mm: selectedProduct?.tile_width_mm || 400
            }
          : m
      )
    }));
  }, [configuredProducts]);

  // Handle useProduct toggle
  const handleUseProductToggle = useCallback((measurementId, useProduct) => {
    setFormData(prev => ({
      ...prev,
      measurements: prev.measurements.map(m => 
        m.id === measurementId
          ? {
              ...m,
              useProduct,
              // Reset product selection if toggling off
              product_id: useProduct ? m.product_id : null,
              product_code: useProduct ? m.product_code : ''
            }
          : m
      )
    }));
  }, []);

  // Remove measurement
  const handleRemoveMeasurement = useCallback((measurementId) => {
    setFormData(prev => ({
      ...prev,
      measurements: prev.measurements.filter(m => m.id !== measurementId)
    }));
  }, []);

  // Validation
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.area_length || parseFloat(formData.area_length) <= 0) {
      newErrors.area_length = 'Valid length is required';
    }

    if (!formData.area_width || parseFloat(formData.area_width) <= 0) {
      newErrors.area_width = 'Valid width is required';
    }

    if (formData.measurements.length === 0) {
      newErrors.measurements = 'At least one tile size is required';
    }

    formData.measurements.forEach((m, index) => {
      if (!m.size_id && !m.size_name) {
        newErrors[`measurement_${index}_size`] = 'Select a size';
      }
      if (!m.tile_length_mm || !m.tile_width_mm || m.tile_length_mm <= 0 || m.tile_width_mm <= 0) {
        newErrors[`measurement_${index}_size`] = 'Please select a valid tile size';
      }
      if (!m.pieces_per_carton || m.pieces_per_carton <= 0) {
        newErrors[`measurement_${index}_pieces`] = 'Invalid pieces per carton';
      }
      if (!m.allocation_value || m.allocation_value <= 0) {
        newErrors[`measurement_${index}_allocation`] = 'Invalid allocation value';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Submit calculation
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setCalculating(true);

      const calculationData = {
        title: formData.title,
        area_length: parseFloat(formData.area_length),
        area_width: parseFloat(formData.area_width),
        unit: formData.unit,
        wastage_percent: parseFloat(formData.wastage_percent),
        customer_id: formData.customer_id,
        tiles: formData.measurements.map(m => {
          const tileData = {
            size_id: m.size_id || null,
            size_name: m.size_name || `${Math.floor(m.tile_length_mm/10)}x${Math.floor(m.tile_width_mm/10)}`,
            pieces_per_carton: parseInt(m.pieces_per_carton) || 4,
            tile_length_mm: parseInt(m.tile_length_mm) || 0,
            tile_width_mm: parseInt(m.tile_width_mm) || 0,
            allocation_type: m.allocation_type || 'area',
            allocation_value: parseFloat(m.allocation_value) || 100,
            // Include product info if selected
            product_id: m.product_id || null,
            product_code: m.product_code || null
          };

          // Validate parsed values
          if (isNaN(tileData.pieces_per_carton) || tileData.pieces_per_carton <= 0) {
            throw new Error(`Invalid pieces per carton for tile: ${m.size_name || 'unnamed'}`);
          }
          if (isNaN(tileData.tile_length_mm) || tileData.tile_length_mm <= 0) {
            throw new Error(`Invalid tile length for tile: ${m.size_name || 'unnamed'}`);
          }
          if (isNaN(tileData.tile_width_mm) || tileData.tile_width_mm <= 0) {
            throw new Error(`Invalid tile width for tile: ${m.size_name || 'unnamed'}`);
          }
          if (isNaN(tileData.allocation_value) || tileData.allocation_value <= 0) {
            throw new Error(`Invalid allocation value for tile: ${m.size_name || 'unnamed'}`);
          }

          return tileData;
        })
      };

      console.log('Sending calculation data:', calculationData);

      const response = await performMultiSizeCalculation(calculationData);

      if (response.success) {
        onCalculationComplete(response.data);
      } else {
        setErrors({ submit: response.message || 'Calculation failed' });
      }
    } catch (error) {
      console.error('Calculation failed:', error);
      console.error('Error details:', error.response?.data);

      const errorMessage = error.response?.data?.message || error.message || 'Calculation failed. Please try again.';
      setErrors({ submit: errorMessage });
    } finally {
      setCalculating(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Calculation Title */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-primary-black mb-2">
          <FileText className="w-4 h-4 text-accent-gold" />
          Calculation Title (Optional)
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="e.g., Main Hallway Design"
          className="w-full px-4 py-3 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
        />
        <p className="text-xs text-primary-slate mt-1">A unique tag for this calculation</p>
      </div>

      {/* Area Dimensions */}
      <div className="p-4 bg-secondary-offWhite rounded-lg">
        <label className="flex items-center gap-2 text-sm font-semibold text-primary-black mb-3">
          <Ruler className="w-4 h-4" />
          Room Dimensions
        </label>
        
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-primary-slate mb-1">Length</label>
            <input
              type="number"
              step="0.01"
              min="0.1"
              value={formData.area_length}
              onChange={(e) => setFormData(prev => ({ ...prev, area_length: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold ${
                errors.area_length ? 'border-red-500' : 'border-secondary-lightGray'
              }`}
              placeholder="Length"
            />
            {errors.area_length && <p className="text-red-500 text-xs mt-1">{errors.area_length}</p>}
          </div>
          
          <div>
            <label className="block text-xs text-primary-slate mb-1">Width</label>
            <input
              type="number"
              step="0.01"
              min="0.1"
              value={formData.area_width}
              onChange={(e) => setFormData(prev => ({ ...prev, area_width: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold ${
                errors.area_width ? 'border-red-500' : 'border-secondary-lightGray'
              }`}
              placeholder="Width"
            />
            {errors.area_width && <p className="text-red-500 text-xs mt-1">{errors.area_width}</p>}
          </div>
          
          <div>
            <label className="block text-xs text-primary-slate mb-1">Unit</label>
            <select
              value={formData.unit}
              onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
              className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
            >
              {UNITS.map(unit => (
                <option key={unit.value} value={unit.value}>{unit.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Wastage */}
        <div className="mt-3">
          <label className="block text-xs text-primary-slate mb-1">
            Wastage Allowance: <span className="font-bold">{formData.wastage_percent}%</span>
          </label>
          <input
            type="range"
            min="0"
            max="30"
            value={formData.wastage_percent}
            onChange={(e) => setFormData(prev => ({ ...prev, wastage_percent: parseInt(e.target.value) }))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-accent-gold"
          />
          <div className="flex justify-between text-xs text-primary-slate">
            <span>0%</span>
            <span>15%</span>
            <span>30%</span>
          </div>
        </div>
      </div>

      {/* Tile Sizes */}
      <div>
        <label className="flex items-center gap-2 text-sm font-semibold text-primary-black mb-3">
          <Layers className="w-4 h-4" />
          Tile Configurations
        </label>
        
        {errors.measurements && (
          <p className="text-red-500 text-sm mb-2">{errors.measurements}</p>
        )}

        <div className="space-y-3">
          {formData.measurements.map((measurement, index) => (
            <div 
              key={measurement.id}
              className={`p-4 rounded-lg border ${
                measurement.isMainFloor 
                  ? 'bg-accent-gold/5 border-accent-gold/30' 
                  : 'bg-secondary-offWhite border-secondary-lightGray'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-primary-black">
                  {measurement.isMainFloor ? '🏠 Main Floor' : `🎨 Design Tile ${index}`}
                </span>
                {!measurement.isMainFloor && (
                  <button
                    type="button"
                    onClick={() => handleRemoveMeasurement(measurement.id)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Product Selection Option */}
              <div className="mb-3 p-3 bg-white rounded-lg border border-secondary-lightGray">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={measurement.useProduct || false}
                    onChange={(e) => handleUseProductToggle(measurement.id, e.target.checked)}
                    className="w-4 h-4 text-accent-gold border-gray-300 rounded focus:ring-accent-gold"
                  />
                  <Package className="w-4 h-4 text-accent-gold" />
                  <span className="text-sm font-medium text-primary-black">Use Configured Product</span>
                </label>
                
                {measurement.useProduct && (
                  <div className="mt-2">
                    {configuredProducts.length === 0 ? (
                      <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                        <Settings className="w-3 h-3 inline mr-1" />
                        No products configured. Please configure products first.
                      </div>
                    ) : (
                      <select
                        value={measurement.product_id || ''}
                        onChange={(e) => handleProductChange(measurement.id, e.target.value)}
                        disabled={loadingProducts}
                        className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold text-sm"
                      >
                        <option value="">Select Product...</option>
                        {configuredProducts.map(prod => (
                          <option key={prod.id} value={prod.id}>
                            {prod.product_code} — {prod.size_name || 'N/A'}
                          </option>
                        ))}
                      </select>
                    )}
                    {measurement.product_id && (
                      <p className="text-xs text-green-600 mt-1">✓ Using: {measurement.product_code}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                {/* Size Selection */}
                <div>
                  <label className="block text-xs text-primary-slate mb-1">Tile Size</label>
                  {measurement.useProduct && measurement.product_id ? (
                    <input
                      type="text"
                      value={measurement.size_name || `${measurement.tile_length_mm}×${measurement.tile_width_mm}mm`}
                      disabled
                      className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                    />
                  ) : (
                    <select
                      value={measurement.size_id || ''}
                      onChange={(e) => handleSizeChange(measurement.id, e.target.value)}
                      disabled={loadingSizes}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold ${
                        errors[`measurement_${index}_size`] ? 'border-red-500' : 'border-secondary-lightGray'
                      }`}
                    >
                      <option value="">Select Size...</option>
                      {sizes.filter(s => s.tile_length_mm && s.tile_width_mm).map(size => (
                        <option key={size.id} value={size.id}>
                          {size.dimension} ({size.tile_length_mm}×{size.tile_width_mm}mm)
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Pieces per Carton */}
                <div>
                  <label className="block text-xs text-primary-slate mb-1">Pieces/Carton</label>
                  <input
                    type="number"
                    min="1"
                    value={measurement.pieces_per_carton}
                    onChange={(e) => handleMeasurementChange(measurement.id, 'pieces_per_carton', e.target.value)}
                    disabled={measurement.useProduct && measurement.product_id}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold ${
                      errors[`measurement_${index}_pieces`] ? 'border-red-500' : 'border-secondary-lightGray'
                    } ${measurement.useProduct && measurement.product_id ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
                  />
                </div>
              </div>

              {/* Allocation (for non-main floor tiles) */}
              {!measurement.isMainFloor && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-primary-slate mb-1">Allocation Type</label>
                    <select
                      value={measurement.allocation_type}
                      onChange={(e) => handleMeasurementChange(measurement.id, 'allocation_type', e.target.value)}
                      className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                    >
                      {ALLOCATION_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-primary-slate mb-1">
                      {measurement.allocation_type === 'cartons' ? 'Cartons' : '% of Area'}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max={measurement.allocation_type === 'area' ? 100 : 1000}
                      value={measurement.allocation_value}
                      onChange={(e) => handleMeasurementChange(measurement.id, 'allocation_value', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold ${
                        errors[`measurement_${index}_allocation`] ? 'border-red-500' : 'border-secondary-lightGray'
                      }`}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add Design Tile Button */}
        <button
          type="button"
          onClick={handleAddDesignTile}
          className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-secondary-lightGray rounded-lg text-primary-slate hover:border-accent-gold hover:text-accent-gold transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Design Tile
        </button>
      </div>

      {/* Error Display */}
      {errors.submit && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {errors.submit}
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={calculating}
        className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-accent-gold text-white rounded-lg font-semibold text-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {calculating ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Calculating...
          </>
        ) : (
          <>
            <Calculator className="w-5 h-5" />
            Calculate
          </>
        )}
      </button>
    </form>
  );
};

export default MultiSizeTileCalculatorForm;
